import { useEffect, useState, useRef, useCallback } from "react"; // Added useCallback
import { LoadBalancerNodeData, HandleConfig } from "../../types"; // Added HandleConfig
import { Position } from "reactflow"; // Added Position
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager

interface LoadBalancerConfigBoxProps {
  nodeId: string;
  data: LoadBalancerNodeData;
  onUpdate?: (data: Partial<LoadBalancerNodeData>) => void;
  onDelete?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

// Load balancer distribution strategies
const DISTRIBUTION_STRATEGIES = [
  { value: "Round Robin", label: "Round Robin" },
  { value: "Least Connections", label: "Least Connections" },
  { value: "IP Hash", label: "IP Hash" },
  { value: "Weighted", label: "Weighted" },
];

const CUSTOM_LABEL_SUGGESTIONS = [
  { value: "Production", label: "Production" },
  { value: "Development", label: "Development" },
  { value: "Staging", label: "Staging" },
  { value: "Enterprise", label: "Enterprise" },
  { value: "Beta", label: "Beta" },
  { value: "Internal", label: "Internal" },
  { value: "High Priority", label: "High Priority" },
  { value: "Low Priority", label: "Low Priority" },
  { value: "Critical", label: "Critical" },
  { value: "Experimental", label: "Experimental" },
  { value: "Legacy", label: "Legacy" },
  { value: "New", label: "New" },
  { value: "Future State", label: "Future State" },
];

const STATUS_SUGGESTIONS = [
  { value: "Live", label: "Live" },
  { value: "Testing", label: "Testing" },
  { value: "Paused", label: "Paused" },
  { value: "Deprecated", label: "Deprecated" },
  { value: "RMN", label: "RMN" },
  { value: "Under Review", label: "Under Review" },
  { value: "Pending Approval", label: "Pending Approval" },
  { value: "Maintenance", label: "Maintenance" },
  { value: "Offline", label: "Offline" },
  { value: "Archived", label: "Archived" },
  { value: "Restricted", label: "Restricted" },
  { value: "Backup", label: "Backup" },
];

// --- Default Handles for Load Balancer ---
const defaultHandles: HandleConfig[] = [
  { id: 'default-target', type: 'target', position: Position.Left, color: '#3B82F6' }, // Blue Target
  { id: 'default-source', type: 'source', position: Position.Right, color: '#3B82F6' }, // Blue Source
];
// --- End Default Handles ---

export default function LoadBalancerConfigBox({
  data,
  nodeId,
  onUpdate,
  onDelete,
  onDetachFromGroup, // Destructure prop
  isAdvancedMode,
  portalRoot,
}: LoadBalancerConfigBoxProps) {
  // Initialize state with props
  const [label, setLabel] = useState(data.label || "");
  const [status, setStatus] = useState(data.status || "");
  const [strategy, setStrategy] = useState(data.strategy || "");
  const [customLabel, setCustomLabel] = useState(data.customLabel || "");
  
  // Initialize visibility settings
  const [statusVisibility, setStatusVisibility] = useState<VisibilityOption>(
    data.statusVisibility === "hidden" ? "hidden" : "visible"
  );
  const [strategyVisibility, setStrategyVisibility] = useState<VisibilityOption>(
    data.strategyVisibility === "hidden" ? "hidden" : "visible"
  );
  const [customLabelVisibility, setCustomLabelVisibility] = useState<VisibilityOption>(
    data.customLabelVisibility === "hidden" ? "hidden" : "visible"
  );

  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() =>
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );
  // --- End Add State for Handles ---

  // Track previous node ID to detect changes
  const prevNodeIdRef = useRef<string | null>(null);

  // Track previous update to prevent loops
  const prevUpdateRef = useRef("");
  // Track if component has initialized to prevent effect firing on mount
  const isInitialized = useRef(false);

  // Initialize or update state from props when the node changes
  useEffect(() => {
    // Extract a unique identifier from the data to detect node changes
    const currentNodeId = (data as any).id || JSON.stringify(data);
    
    // If the node has changed or this is the first initialization
    if (prevNodeIdRef.current !== currentNodeId) {
      setLabel(data.label || "");
      setStatus(data.status || "");
      setStrategy(data.strategy || "");
      setCustomLabel(data.customLabel || "");
      
      // Initialize visibility settings
      setStatusVisibility(data.statusVisibility === "hidden" ? "hidden" : "visible");
      setStrategyVisibility(data.strategyVisibility === "hidden" ? "hidden" : "visible");
      setCustomLabelVisibility(data.customLabelVisibility === "hidden" ? "hidden" : "visible");

      // Sync handles state more carefully
      if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
          setHandles([...data.handles]);
      }
      // Do not reset to defaultHandles here

      // Mark as initialized after the first sync
      if (!isInitialized.current) {
          isInitialized.current = true;
      }
    }
  }, [data]);

  // Update parent when state changes
  useEffect(() => {
    // Prevent firing on initial mount/effect synchronization
    if (!isInitialized.current) {
      return;
    }

    const updates = {
      label,
      // Cast status to the expected type or undefined
      status: (status as 'active' | 'inactive' | 'connected' | 'pending' | 'error' | undefined),
      strategy,
      customLabel,
      statusVisibility,
      strategyVisibility,
      customLabelVisibility,
      handles: Array.isArray(handles) ? handles : [], // Add handles to update
    };

    const updatesJson = JSON.stringify(updates);
    if (updatesJson !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = updatesJson;
      onUpdate(updates);
    }
  }, [
    label, 
    status, 
    strategy, 
    customLabel,
    statusVisibility,
    strategyVisibility,
    customLabelVisibility,
    handles, // Add handles dependency
    onUpdate
  ]);

  // Toggle visibility handlers
  const toggleStatusVisibility = () => {
    setStatusVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  const toggleStrategyVisibility = () => {
    setStrategyVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  const toggleCustomLabelVisibility = () => {
    setCustomLabelVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);
  // --- End Handler ---

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDelete} // Temporary using onDelete as onDuplicate for demonstration
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Name
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Environment"
          visibility={statusVisibility}
          onToggleVisibility={toggleStatusVisibility}
          className="mb-1"
        />
        <AutoComplete
          value={status}
          onChange={setStatus}
          options={STATUS_SUGGESTIONS}
          placeholder="Set a Status Label"
          dropdownClassName="absolute z-[9999]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Distribution Strategy"
          visibility={strategyVisibility}
          onToggleVisibility={toggleStrategyVisibility}
          className="mb-1"
        />
        <AutoComplete
          value={strategy}
          onChange={setStrategy}
          options={DISTRIBUTION_STRATEGIES}
          placeholder="Specify a Distribution Strategy"
          dropdownClassName="absolute z-[9999]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={customLabelVisibility}
          onToggleVisibility={toggleCustomLabelVisibility}
          className="mb-1"
        />
        <AutoComplete
          value={customLabel}
          onChange={setCustomLabel}
          options={CUSTOM_LABEL_SUGGESTIONS}
          placeholder="Set a Custom Label"
          dropdownClassName="absolute z-[9999]"
          portalContainer={portalRoot}
        />
      </div>

      {/* --- Render Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles} // Use handles state
          onChange={handleHandlesChange}
          maxHandles={6} // Adjust as needed
          portalRoot={portalRoot}
        />
      </div>
      {/* --- End Handle Manager --- */}
    </div>
  );
}
